// client/src/pages/Admin/ProjectManager/ProjectManager.tsx
/**
 * PROJECT MANAGER MAIN COMPONENT
 * 
 * Orchestrates all project management functionality including:
 * - Project listing with pagination
 * - Filtering and search
 * - Bulk actions
 * - Create, read, update, delete operations
 * - Statistics and analytics
 * 
 * @module ProjectManager
 */

import React, { useState, useCallback, useMemo } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import { Project } from './types/project.types';
import { ProjectStatus as ProjectStatusEnum } from '../../../shared/models/Project';
import { ProjectEditor } from '../ProjectEditor';

// Hooks
import { useProjects } from './hooks/useProjects';
import { useProjectFilters } from './hooks/useProjectFilters';
import { usePagination } from './hooks/usePagination';
import { useBulkSelection } from './hooks/useBulkSelection';
import { useDebounce } from './hooks/useDebounce';

// Components
import { ProjectHeader } from './components/ProjectHeader';
import { ProjectStats } from './components/ProjectStats';
import { ProjectToolbar } from './components/ProjectToolbar';
import { ProjectFilters } from './components/ProjectFilters';
import { ProjectGrid } from './components/ProjectGrid';
import { ProjectPagination } from './components/ProjectPagination';
import { ProjectSkeleton } from './components/ProjectSkeleton';
import { BulkActionToolbar } from './components/BulkActionToolbar';

// Utils
import { projectMatchesSearch } from './utils/projectSearch';
import { getProjectStats, getTotalViews } from './utils/projectStatistics';
import { formatProjectForExport } from './utils/projectHelpers';

// Constants
import { DEFAULT_PAGE_SIZE, DEBOUNCE_DELAY, SORT_OPTIONS } from './constants/project.constants';

// Services
import { projectService } from './services/projectService';

// Types
import { ModalState } from './types/project.types';

export const ProjectManager: React.FC = () => {
    // ============================================
    // STATE
    // ============================================
    
    const [editingProject, setEditingProject] = useState<Project | null>(null);
    const [showEditor, setShowEditor] = useState<boolean>(false);
    const [isBulkActionLoading, setIsBulkActionLoading] = useState<boolean>(false);
    const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
    const [modalState, setModalState] = useState<ModalState | null>(null);

    // ============================================
    // CUSTOM HOOKS
    // ============================================
    
    const { projects, loading, error, loadProjects, removeProject, updateProjectStatus, addProject, updateProject } = useProjects();
    
    const {
        filters,
        filteredProjects,
        hasActiveFilters,
        activeFilterCount,
        updateFilter,
        resetFilters,
        sortOption,
        sortDirection,
        setSortOption,
        toggleSortDirection,
        getFilterOptions,
        searchTerm,
        setSearchTerm
    } = useProjectFilters(projects);

    const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);
    const filterOptions = useMemo(() => getFilterOptions(), [getFilterOptions]);

    // ============================================
    // PAGINATION
    // ============================================
    
    const {
        currentPage,
        pageSize,
        totalPages,
        paginatedItems,
        startIndex,
        endIndex,
        pageNumbers,
        goToPage,
        goToFirstPage,
        goToLastPage,
        goToPreviousPage,
        goToNextPage,
        changePageSize,
        resetPagination
    } = usePagination(filteredProjects, DEFAULT_PAGE_SIZE);

    // ============================================
    // BULK SELECTION
    // ============================================
    
    const {
        selectedIds,
        isAllSelected,
        selectedCount,
        selectedIdsList,
        toggleSelection,
        toggleSelectAll,
        clearSelection,
        setSelectedIds
    } = useBulkSelection(paginatedItems);

    // ============================================
    // STATISTICS
    // ============================================
    
    const stats = useMemo(() => getProjectStats(projects), [projects]);
    const totalViews = useMemo(() => getTotalViews(projects), [projects]);

    // ============================================
    // HANDLERS
    // ============================================
    
    const handleOpenNewProject = useCallback(() => {
        setEditingProject(null);
        setShowEditor(true);
    }, []);

    const handleOpenEditProject = useCallback((project: Project) => {
        setEditingProject(project);
        setShowEditor(true);
        setOpenActionMenu(null);
    }, []);

    const handleCloseEditor = useCallback(() => {
        setShowEditor(false);
        setEditingProject(null);
    }, []);

    const handleEditorSave = useCallback(async () => {
        await loadProjects();
        handleCloseEditor();
        clearSelection();
    }, [loadProjects, handleCloseEditor, clearSelection]);

    const handleDeleteProject = useCallback((id: string) => {
        const projectToDelete = projects.find(p => p._id === id);
        const projectTitle = projectToDelete?.title || 'this project';

        setModalState({
            isOpen: true,
            type: 'danger',
            title: 'Delete Project',
            message: `Are you sure you want to delete "${projectTitle}"? This action cannot be undone.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                try {
                    await removeProject(id);
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to delete project';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    // Show error toast here
                    console.error(errorMsg);
                }
            },
            isLoading: false
        });
    }, [projects, removeProject, clearSelection]);

    const handleCloseModal = useCallback(() => {
        setModalState(null);
    }, []);

    const handleStatusChange = useCallback(async (id: string, status: ProjectStatusEnum) => {
        try {
            await updateProjectStatus(id, status);
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to update status';
            console.error(errorMsg);
        }
    }, [updateProjectStatus]);

    const handleBulkPublish = useCallback(async () => {
        if (selectedIdsList.length === 0) return;

        setModalState({
            isOpen: true,
            type: 'warning',
            title: 'Bulk Publish',
            message: `Are you sure you want to publish ${selectedIdsList.length} selected project(s)?`,
            confirmText: 'Publish',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                setIsBulkActionLoading(true);
                try {
                    await projectService.bulkUpdateProjects(selectedIdsList, { status: ProjectStatusEnum.PUBLISHED });
                    await loadProjects();
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to publish projects';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    console.error(errorMsg);
                } finally {
                    setIsBulkActionLoading(false);
                }
            },
            isLoading: false
        });
    }, [selectedIdsList, loadProjects, clearSelection]);

    const handleBulkArchive = useCallback(async () => {
        if (selectedIdsList.length === 0) return;

        setModalState({
            isOpen: true,
            type: 'warning',
            title: 'Bulk Archive',
            message: `Are you sure you want to archive ${selectedIdsList.length} selected project(s)?`,
            confirmText: 'Archive',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                setIsBulkActionLoading(true);
                try {
                    await projectService.bulkUpdateProjects(selectedIdsList, { status: ProjectStatusEnum.ARCHIVED });
                    await loadProjects();
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to archive projects';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    console.error(errorMsg);
                } finally {
                    setIsBulkActionLoading(false);
                }
            },
            isLoading: false
        });
    }, [selectedIdsList, loadProjects, clearSelection]);

    const handleBulkFeature = useCallback(async () => {
        if (selectedIdsList.length === 0) return;

        setModalState({
            isOpen: true,
            type: 'warning',
            title: 'Bulk Feature',
            message: `Are you sure you want to feature ${selectedIdsList.length} selected project(s)?`,
            confirmText: 'Feature',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                setIsBulkActionLoading(true);
                try {
                    await projectService.bulkUpdateProjects(selectedIdsList, { featured: true });
                    await loadProjects();
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to feature projects';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    console.error(errorMsg);
                } finally {
                    setIsBulkActionLoading(false);
                }
            },
            isLoading: false
        });
    }, [selectedIdsList, loadProjects, clearSelection]);

    const handleBulkUnfeature = useCallback(async () => {
        if (selectedIdsList.length === 0) return;

        setModalState({
            isOpen: true,
            type: 'warning',
            title: 'Bulk Unfeature',
            message: `Are you sure you want to unfeature ${selectedIdsList.length} selected project(s)?`,
            confirmText: 'Unfeature',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                setIsBulkActionLoading(true);
                try {
                    await projectService.bulkUpdateProjects(selectedIdsList, { featured: false });
                    await loadProjects();
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to unfeature projects';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    console.error(errorMsg);
                } finally {
                    setIsBulkActionLoading(false);
                }
            },
            isLoading: false
        });
    }, [selectedIdsList, loadProjects, clearSelection]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedIdsList.length === 0) return;

        setModalState({
            isOpen: true,
            type: 'danger',
            title: 'Bulk Delete',
            message: `Are you sure you want to permanently delete ${selectedIdsList.length} selected project(s)? This action cannot be undone.`,
            confirmText: 'Delete All',
            cancelText: 'Cancel',
            onConfirm: async () => {
                setModalState(prev => prev ? { ...prev, isLoading: true } : null);
                setIsBulkActionLoading(true);
                try {
                    await projectService.bulkDeleteProjects(selectedIdsList);
                    await loadProjects();
                    clearSelection();
                    setModalState(null);
                } catch (err) {
                    const errorMsg = err instanceof Error ? err.message : 'Failed to delete projects';
                    setModalState(prev => prev ? { ...prev, isLoading: false } : null);
                    console.error(errorMsg);
                } finally {
                    setIsBulkActionLoading(false);
                }
            },
            isLoading: false
        });
    }, [selectedIdsList, loadProjects, clearSelection]);

    const handleBulkExport = useCallback(() => {
        if (selectedIdsList.length === 0) return;

        try {
            const selectedProjects = projects.filter(p => p._id && selectedIds.has(p._id));
            const exportData = selectedProjects.map(formatProjectForExport);
            
            const jsonStr = JSON.stringify(exportData, null, 2);
            const blob = new Blob([jsonStr], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `projects-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export projects:', err);
        }
    }, [selectedIdsList, projects, selectedIds]);

    const handleGetMatchFields = useCallback((project: Project): string[] => {
        if (!debouncedSearchTerm || !project) return [];
        try {
            const result = projectMatchesSearch(project, debouncedSearchTerm);
            return result.matchFields || [];
        } catch {
            return [];
        }
    }, [debouncedSearchTerm]);

    const handleToggleActionMenu = useCallback((id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setOpenActionMenu(prev => prev === id ? null : id);
    }, []);

    // ============================================
    // RENDER
    // ============================================
    
    return (
        <div className="project-manager">
            {/* Modal */}
            {modalState && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h2>{modalState.title}</h2>
                        <p>{modalState.message}</p>
                        <div className="modal-actions">
                            <button
                                className="btn-secondary"
                                onClick={handleCloseModal}
                                disabled={modalState.isLoading}
                            >
                                {modalState.cancelText || 'Cancel'}
                            </button>
                            <button
                                className={`btn-${modalState.type}`}
                                onClick={modalState.onConfirm}
                                disabled={modalState.isLoading}
                            >
                                {modalState.isLoading ? 'Loading...' : (modalState.confirmText || 'Confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <ProjectHeader
                onRefresh={loadProjects}
                onNewProject={handleOpenNewProject}
                projectsCount={projects.length}
            />

            {/* Stats */}
            <ProjectStats
                stats={stats}
                totalViews={totalViews}
                totalDownloads={0}
            />

            {/* Error Message */}
            {error && (
                <div className="error-message" role="alert">
                    <span className="error-icon"><AlertTriangle size={16} /></span>
                    {error}
                </div>
            )}

            {/* Toolbar */}
            <ProjectToolbar
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                activeFilterCount={activeFilterCount}
                hasActiveFilters={hasActiveFilters}
                onResetFilters={resetFilters}
                sortOption={sortOption}
                sortDirection={sortDirection}
                onSortChange={setSortOption}
                onToggleSortDirection={toggleSortDirection}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onClearSearch={() => {
                    setSearchTerm('');
                }}
                isSearchFocused={isSearchFocused}
                onSearchFocus={() => setIsSearchFocused(true)}
                onSearchBlur={() => setIsSearchFocused(false)}
            />

            {/* Filters Panel */}
            {showFilters && (
                <ProjectFilters
                    filters={filters}
                    filterOptions={filterOptions}
                    onFilterChange={updateFilter}
                    hasActiveFilters={hasActiveFilters}
                    onResetFilters={resetFilters}
                />
            )}

            {/* Bulk Action Toolbar */}
            <BulkActionToolbar
                selectedCount={selectedCount}
                isBulkActionLoading={isBulkActionLoading}
                onPublish={handleBulkPublish}
                onArchive={handleBulkArchive}
                onFeature={handleBulkFeature}
                onUnfeature={handleBulkUnfeature}
                onExport={handleBulkExport}
                onDelete={handleBulkDelete}
                onClear={clearSelection}
            />

            {/* Results Count */}
            <div className="results-count">
                <span>
                    Showing <strong>{startIndex}-{Math.min(endIndex, filteredProjects.length)}</strong> of <strong>{filteredProjects.length}</strong> projects
                    {hasActiveFilters && ' (filtered)'}
                </span>
                {debouncedSearchTerm && (
                    <span className="search-stats">
                        <Search size={14} />
                        "{debouncedSearchTerm}"
                    </span>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <ProjectSkeleton count={pageSize} />
            ) : (
                <>
                    <ProjectGrid
                        projects={paginatedItems}
                        selectedIds={selectedIds}
                        onToggleSelection={toggleSelection}
                        onEdit={handleOpenEditProject}
                        onDelete={handleDeleteProject}
                        onStatusChange={handleStatusChange}
                        onToggleActionMenu={handleToggleActionMenu}
                        openActionMenu={openActionMenu}
                        getMatchFields={handleGetMatchFields}
                        debouncedSearchTerm={debouncedSearchTerm}
                        hasActiveFilters={hasActiveFilters}
                    />

                    {filteredProjects.length > 0 && (
                        <ProjectPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            pageSize={pageSize}
                            startIndex={startIndex}
                            endIndex={Math.min(endIndex, filteredProjects.length)}
                            totalItems={filteredProjects.length}
                            pageNumbers={pageNumbers}
                            onGoToPage={goToPage}
                            onGoToFirstPage={goToFirstPage}
                            onGoToLastPage={goToLastPage}
                            onGoToPreviousPage={goToPreviousPage}
                            onGoToNextPage={goToNextPage}
                            onPageSizeChange={changePageSize}
                        />
                    )}
                </>
            )}

            {/* Editor Modal */}
            {showEditor && (
                <ProjectEditor
                    project={editingProject || undefined}
                    onSave={handleEditorSave}
                    onCancel={handleCloseEditor}
                />
            )}
        </div>
    );
};

export default ProjectManager;